/**
 * Tool UI for batch_render_video — a single tool call that renders many videos,
 * each in its own dedicated Modal worker container (real parallel CPU). Shows:
 *  - Live per-job progress while running (n/total + current label), driven by
 *    the `batch-progress` SSE channel (scoped to this tool).
 *  - A per-job result list on completion with ✓ / ✗ and the saved path + format
 *    or error, plus a succeeded/failed tally so failures are obvious at a glance.
 *
 * Mirrors BatchScreenshotToolUI; differs in the video-shaped result (frames /
 * format / fileSize) and the `preparing` phase (project materialize before the
 * worker picks it up).
 */
import { useState, useEffect } from 'react';
import { makeAssistantToolUI } from '@assistant-ui/react';
import { useBatchProgressStream } from '../useShellOutputStream';
import { Film, XCircle, Check, Circle } from 'lucide-react';
import { ToolRow, Shimmer, DetailPanel, DetailPre, plainText } from '../toolRow';

interface JobArg {
  directory: string;
  saveToWorkspace: string;
  label?: string;
}
interface BatchArgs {
  jobs?: JobArg[];
  perJobTimeoutMs?: number;
  maxWorkers?: number;
  _thought?: string;
}
interface JobResult {
  index: number;
  label?: string;
  directory?: string;
  savedPath?: string;
  outputFormat?: string;
  resolution?: string;
  frames?: number;
  fileSize?: number;
  jsErrors?: string[];
  error?: string;
}
interface BatchResult {
  status?: 'succeeded' | 'partial' | 'failed';
  total?: number;
  succeeded?: number;
  failed?: number;
  videos?: JobResult[];
  durationMs?: number;
  error?: string;
}

function fmtBytes(n?: number): string | undefined {
  if (!n || n <= 0) return undefined;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${Math.round((n / (1024 * 1024)) * 10) / 10} MB`;
}

export const BatchRenderVideoToolUI = makeAssistantToolUI<BatchArgs, BatchResult>({
  toolName: 'batch_render_video',
  render: function BatchRenderVideoRender(props: any) {
    const { args, result, status } = props;
    const isRunning = status.type === 'running';
    const [expanded, setExpanded] = useState(true);

    const live = useBatchProgressStream('batch_render_video');
    const total = result?.total ?? args?.jobs?.length ?? live.find((e) => e.total)?.total ?? 0;

    // Auto-collapse once finished and everything passed.
    useEffect(() => {
      if (!isRunning && result && !result.failed) setExpanded(false);
    }, [isRunning, result?.failed]);

    const failed = (result?.failed ?? 0) > 0 || !!result?.error;
    const succeeded = !!result && !failed;

    // "Done" = anything past the render phase (done | error).
    const liveDoneCount = live.filter((e) => e.status === 'done' || e.status === 'error').length;
    const liveCurrent = [...live].reverse().find((e) => e.status === 'rendering' || e.status === 'preparing');

    return (
      <ToolRow
        icon={!isRunning && failed ? <XCircle className="text-red-500" /> : <Film />}
        label={plainText(args?._thought) || `Batch render${total ? ` · ${total} video${total === 1 ? '' : 's'}` : ''}`}
        running={isRunning}
        tone={!isRunning && failed ? 'error' : 'default'}
        meta={
          isRunning ? `${total ? `${liveDoneCount}/${total}` : ''}${liveCurrent?.label ? ` · ${liveCurrent.label}` : ''}` || undefined
            : succeeded ? `${result!.succeeded}/${result!.total}`
            : failed ? <span className="text-red-600">{result?.failed ?? '!'} failed</span>
            : undefined
        }
        open={expanded}
        onToggle={() => setExpanded((e) => !e)}
      >
          <div className="space-y-1">
            {/* Live rows while running */}
            {isRunning && live.length > 0 && live.map((e) => (
              <ProgressRow
                key={e.index}
                label={e.label || `video ${e.index + 1}`}
                status={e.status}
                detail={e.status === 'error' ? e.error : e.savedPath}
              />
            ))}
            {isRunning && live.length === 0 && (
              <div className="text-xs text-zinc-400">Spinning up render workers…</div>
            )}

            {/* Final per-job results */}
            {!isRunning && result?.videos?.map((v: JobResult) => (
              <ProgressRow
                key={v.index}
                label={v.label || `video ${v.index + 1}`}
                status={v.error ? 'error' : 'done'}
                detail={v.error
                  ? v.error
                  : [v.savedPath, v.resolution, v.outputFormat?.toUpperCase(), fmtBytes(v.fileSize)]
                      .filter(Boolean).join(' · ')}
                warn={v.jsErrors && v.jsErrors.length > 0 ? `${v.jsErrors.length} JS error(s)` : undefined}
              />
            ))}

            {!isRunning && result?.error && (
              <DetailPanel label="Error" tone="error"><DetailPre tone="error">{result.error}</DetailPre></DetailPanel>
            )}
            {!isRunning && result?.durationMs != null && (
              <div className="text-xs text-zinc-400">{Math.round(result.durationMs / 100) / 10}s total</div>
            )}
          </div>
      </ToolRow>
    );
  },
});

function ProgressRow({
  label, status, detail, warn,
}: { label: string; status: 'preparing' | 'rendering' | 'done' | 'error'; detail?: string; warn?: string }) {
  return (
    <div className="flex items-center gap-2 text-meta">
      {status === 'rendering' || status === 'preparing' ? (
        <Circle className="w-3 h-3 text-zinc-300 shrink-0" />
      ) : status === 'error' ? (
        <XCircle className="w-3 h-3 text-red-500 shrink-0" />
      ) : (
        <Check className="w-3 h-3 text-zinc-400 shrink-0" />
      )}
      <span className="text-zinc-700 shrink-0">{status === 'rendering' || status === 'preparing' ? <Shimmer>{label}</Shimmer> : label}</span>
      {status === 'preparing' && <span className="text-xs text-zinc-400 shrink-0">preparing…</span>}
      {detail && (
        <span className={`font-mono text-xs truncate ${status === 'error' ? 'text-red-600' : 'text-zinc-400'}`}>{detail}</span>
      )}
      {warn && <span className="text-xs text-yellow-600 shrink-0">{warn}</span>}
    </div>
  );
}
